// Pure helpers and shared types for CallOffWizard.tsx, split out to keep
// CallOffWizard.tsx under the 500-line cap. No behaviour change from the
// original inline definitions — moved verbatim.
import type { CallOffLine, WizardDelivery } from '../../../lib/api';

export interface Supplier {
  id: number;
  name: string;
  supplier_code: string;
}

export interface SupplierStat {
  supplier__id: number;
  supplier__name: string;
  total: number;
  active: number;
}

export type WizardStep = 1 | 2 | 3;

export function computeFillPercent(deliveries: WizardDelivery[]): number {
  let totalFill = 0;
  for (const delivery of deliveries) {
    for (const part of delivery.parts) {
      if (part.loadQty > 0) {
        totalFill += part.qty / part.loadQty;
      }
    }
  }
  return Math.round(totalFill * 100);
}

export function computeTotalQty(deliveries: WizardDelivery[]): number {
  let total = 0;
  for (const delivery of deliveries) {
    for (const part of delivery.parts) {
      total += part.qty;
    }
  }
  return total;
}

/**
 * Group flat call-off lines back into wizard delivery objects,
 * grouped by delivery_type + delivery_destination + delivery_address +
 * sales_order — the same identity the backend matches lines on. Leaving
 * sales_order out merged two drops to one address that belonged to different
 * sales orders, and re-saving stamped the first order's number over both.
 *
 * Destinations are preserved as-is from the backend. Stale destinations
 * (warehouse FKs that no longer resolve to an active warehouse) are detected
 * separately by the wizard against the fetched warehouse list and surfaced
 * to the user — never silently rewritten here.
 *
 * @param lines - the call-off's lines
 * @param lineIdsOnAsn - CR-8613: lines already on an ASN; their Priority tick
 *   is fixed in the wizard
 * @returns one wizard delivery per destination
 */
export function groupLinesToDeliveries(
  lines: CallOffLine[], lineIdsOnAsn: readonly number[] = [],
): WizardDelivery[] {
  const onAsn = new Set(lineIdsOnAsn);
  const map = new Map<string, WizardDelivery>();

  for (const line of lines) {
    const key = `${line.delivery_type}|${line.delivery_destination ?? ''}|${line.delivery_address}|${line.sales_order ?? ''}`;
    if (!map.has(key)) {
      // For direct deliveries, the address was stored as "address, postcode"
      // Split it back into separate fields for the wizard
      let address = line.delivery_address;
      let postcode = '';
      if (line.delivery_type === 'direct' && address) {
        const lastComma = address.lastIndexOf(', ');
        if (lastComma !== -1) {
          postcode = address.slice(lastComma + 2);
          address = address.slice(0, lastComma);
        }
      }

      map.set(key, {
        type: line.delivery_type,
        destinationId: line.delivery_destination,
        destinationName: line.delivery_destination_name || address || 'Delivery',
        // Older direct lines carry no stored name, so the address stands in for
        // display only — saving it back would put a street address in the
        // ship-to name (CR-7144).
        destinationNameIsFallback: !line.delivery_destination_name,
        salesOrderId: line.sales_order || null,
        address,
        postcode,
        deliveryDate: '',
        parts: [],
      });
    }
    const delivery = map.get(key)!;
    delivery.parts.push({
      partId: line.part_id ?? null,
      partNumber: line.part_number,
      description: line.description,
      qty: parseFloat(line.requested_qty) || 0,
      palletQty: parseFloat(line.pallet_qty) || 0,
      loadQty: parseFloat(line.load_qty) || 0,
      isPriority: Boolean(line.is_priority),
      priorityLocked: onAsn.has(line.id),
    });
  }

  return Array.from(map.values());
}
